import React, { useState, useEffect } from 'react';
import { History, AlertCircle, CheckCircle, Clock, XCircle, Tag, Loader2, BookOpen, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { UserInterview } from '../../services/interviewService';
import { Button } from '../common/Button';

// Updated DateFilter interface
interface DateFilter {
  value: string;
  label: string;
  fromDate?: string;
  toDate?: string;
}

interface InterviewHistoryProps {
  interviews: UserInterview[];
  dateFilters: DateFilter[];
  selectedDateFilter: string;
  onDateFilterChange: (filter: string) => void;
  onViewFeedback: (interviewId: string) => void;
  className?: string;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRejoinInterview?: (interviewId: string) => void; // Opens the interview in the same tab
  onRescheduleInterview?: (interviewId: string) => void; // Opens the reschedule modal
}

export const InterviewHistory: React.FC<InterviewHistoryProps> = ({
  interviews,
  dateFilters,
  selectedDateFilter,
  onDateFilterChange,
  onViewFeedback,
  onRejoinInterview,
  onRescheduleInterview,
  className = '',
  isLoading = false,
  error = null,
  onRetry
}) => {
  const { isMobile: isMobileView } = useResponsive();
  
  // Pagination state - now fully internal
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset pagination when interviews change
  useEffect(() => {
    console.log('InterviewHistory: All interviews with status in_progress', interviews.filter((interview) => interview.status === 'in_progress'));
    setCurrentPage(1);
  }, [interviews.length, selectedDateFilter]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString: string) => {
    try {
      // Check if the date string is literally 'Invalid date'
      if (dateString === 'Invalid date') {
        return 'Invalid date';
      }
      
      // Validate that the date string is valid
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('InterviewHistory: Invalid date encountered:', dateString);
        return dateString;
      }
      
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      console.error('InterviewHistory: Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  // Function to get the most relevant date based on interview status
  const getRelevantDate = (interview: UserInterview): string => {
    switch (interview.status) {
      case 'completed':
        // For completed interviews, show completion time if available, otherwise fallback to scheduled time
        return interview.completedAt || interview.scheduledAt;
      case 'not_started':
        return interview.createdAt;
      case 'in_progress':
        // For in-progress interviews, show start time if available, otherwise scheduled time
        return interview.startedAt || interview.scheduledAt;
      case 'cancelled':
        return interview.scheduledAt || interview.createdAt;
      case 'expired':
        return interview.scheduledAt || interview.createdAt;
      case 'scheduled':
        return interview.scheduledAt;
      default:
        // For other statuses, show scheduled time
        return interview.scheduledAt;
    }
  };

  // Function to get the appropriate date label
  const getDateLabel = (interview: UserInterview): string => {
    switch (interview.status) {
      case 'completed':
        return '';
      case 'in_progress':
        return '';
      case 'scheduled':
        return 'Scheduled';
      case 'cancelled':
        return 'Cancelled';
      case 'expired':
        return '';
      case 'not_started':
        return '';
      default:
        return '';
    }
  };

  const getStatusInfo = (scheduledAt: string, status?: string, isActive?: boolean) => {
    console.log('getStatusInfo: status, isActive', status, isActive);
    // If interview is marked as inactive, override the status
    if (isActive === false) {
      return {
        iconType: 'alert-circle',
        text: 'Inactive',
        color: 'text-gray-500',
        bgColor: 'bg-gray-500/10'
      };
    }
    
    switch (status) {
      case 'completed':
        return {
          iconType: 'check-circle',
          text: 'Completed',
          color: 'text-green-500',
          bgColor: 'bg-green-500/10'
        };
      case 'in_progress':
        if (isInterviewJoinable(scheduledAt)) {
        return {
          iconType: 'clock',
          text: 'In Progress',
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10'
        };
      } else {
        return {
          iconType: 'clock',
          text: 'Expired',
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10'
        };
      }
      case 'cancelled':
        return {
          iconType: 'x-circle',
          text: 'Cancelled',
          color: 'text-red-500',
          bgColor: 'bg-red-500/10'
        };
      case 'expired':
        return {
          iconType: 'x-circle',
          text: 'No Appearance',
          color: 'text-red-500',
          bgColor: 'bg-red-500/10'
        };
      case 'not_started':
        return {
          iconType: 'clock',
          text: 'Not Started',
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10'
        };
      default:
        return {
          iconType: 'alert-circle',
          text: 'Unknown',
          color: 'text-gray-500',
          bgColor: 'bg-gray-500/10'
        };
    }
  };

  const isInterviewJoinable = (scheduledAt: string) => {
    const scheduledTime = new Date(scheduledAt).getTime();
    const currentTime = new Date().getTime();
    const tenMinutesInFutureMs = 10 * 60 * 1000;
    const fiveMinutesInPastMs = -5 * 60 * 1000;
    
    const isInFuture = currentTime >= scheduledTime && currentTime <= scheduledTime + tenMinutesInFutureMs; // 10 minutes after scheduledAt
    const isInPast = currentTime >= scheduledTime + fiveMinutesInPastMs && currentTime <= scheduledTime; // 5 minutes before scheduledAt
    return isInFuture || isInPast;
  };
  
  // No need to filter interviews on client side since it's done on server now
  // But filter out 'not_started' interviews as they should only show in upcoming interviews
  const filteredInterviews = interviews.filter(interview => 
    interview.status !== 'not_started' && interview.status !== 'scheduled'
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredInterviews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInterviews = filteredInterviews.slice(startIndex, endIndex);

  // Handle page change - now fully internal
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Smart pagination function to generate page numbers with ellipsis
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = isMobileView ? 3 : 5; // Show fewer pages on mobile
    
    if (totalPages <= maxVisiblePages + 2) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage, endPage;
      
      if (currentPage <= 3) {
        // If current page is near the beginning
        startPage = 2;
        endPage = Math.min(maxVisiblePages, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        // If current page is near the end
        startPage = Math.max(totalPages - maxVisiblePages + 1, 2);
        endPage = totalPages - 1;
      } else {
        // If current page is in the middle
        const around = Math.floor((maxVisiblePages - 1) / 2);
        startPage = Math.max(currentPage - around, 2);
        endPage = Math.min(currentPage + around, totalPages - 1);
      }
      
      // Add ellipsis before start if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after end if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Render content based on loading and error states
  const renderContent = () => {
    if (error) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            {React.createElement(AlertCircle, { size: 24, className: "mx-auto text-red-500 mb-2" })}
            <p className="text-red-500 mb-2">{error}</p>
            {onRetry && (
              <button 
                className="px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md hover:bg-primary-light/90 dark:hover:bg-primary-dark/90 transition"
                onClick={onRetry}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      );
    }
    
    // Normal list view display when there's no error
    return (
      <>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex flex-col justify-center items-center mb-10">
              {React.createElement(Loader2, { size: 24, className: "animate-spin mx-auto text-primary-light dark:text-primary-dark mb-2" })}
              <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">Loading interview history...</p>
            </div>
          ) : (
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-border-light dark:border-border-dark">
                <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Date</th>
                <th className="text-sm text-left py-3 px-6 text-text-secondary-light dark:text-text-secondary-dark font-medium">Interview</th>
                {/* Duration header hidden as it can be confusing for users
                <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Duration (used)</th> */}
                <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Status</th>
                <th className="text-sm text-left py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Score</th>
                <th className="text-sm text-right py-3 px-4 text-text-secondary-light dark:text-text-secondary-dark font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentInterviews.sort((a, b) => new Date(b.completedAt || b.startedAt || b.scheduledAt).getTime() - new Date(a.completedAt || a.startedAt || a.scheduledAt).getTime()).map((interview) => {
                try {
                  if (!interview || !interview._id || !interview.interview) {
                    console.warn('InterviewHistory: Invalid interview data:', interview);
                    return null;
                  }
                  
                  const statusInfo = getStatusInfo(interview.scheduledAt, interview.status, interview.interview && interview.interview.isActive);
                  
                  // Add debug logging to track the values being used for rendering
                  console.log('[InterviewHistory] Rendering interview row:', {
                    id: interview._id,
                    status: interview.status,
                    series: interview.series?.name,
                    isActive: interview.interview && interview.interview.isActive,
                    statusInfo
                  });
                  
                  return (
                    <tr key={interview._id} className="text-base border-b border-border-light/25 dark:border-border-dark/25 hover:bg-surface-light/25 dark:hover:bg-surface-dark/25 transition-colors">
                      <td className="py-4 px-4 min-w-[140px]">
                        <div className="flex flex-col items-start gap-1">
                          <div className="text-base text-text-primary-light dark:text-text-primary-dark">
                            {formatDate(getRelevantDate(interview))}
                          </div>
                          <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            {formatTime(getRelevantDate(interview))}
                          </div>
                          <div className="text-xs text-purple-600 dark:text-purple-400">
                            {getDateLabel(interview)}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 min-w-[320px]">
                        <div className="font-medium text-text-primary-light dark:text-text-primary-dark">{interview.interview.title || 'Untitled Interview'}</div>
                        <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            {interview.series ? interview.series.role || interview.series.jobRoleTitle || 'N/A' : interview.interview.role || 'N/A'} • {interview.series ? interview.series.company || 'N/A' : interview.interview.company || 'N/A'}
                        </div>
                        <div className="flex gap-2 mt-1 items-center">
                          <div className="inline-flex items-center px-2 py-1 rounded text-xs bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark">
                          {React.createElement(Globe, { size: 12, className: "mr-1" })}
                            {interview.languageName || interview.language || 'English'}
                          </div>
                          {/* Only show series badge if series information is available */}
                          {interview.series && (
                            <div className="inline-flex items-center px-2 py-1 bg-purple-500/10 rounded text-xs text-purple-600 dark:text-purple-400">
                              {React.createElement(BookOpen, { size: 12, className: "mr-1" })}
                              {interview.series.name.length > 36 ? interview.series.name.substring(0, 36) + '...' : interview.series.name}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Duration display hidden as it can be confusing for users
                      <td className="py-4 px-4 min-w-[140px]">{interview.minutesUsed} mins</td> */}
                      <td className="py-4 px-4 min-w-[140px]">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${statusInfo.bgColor}`}>
                          {statusInfo.iconType === 'check-circle' && (
                            React.createElement(CheckCircle, { className: `w-4 h-4 ${statusInfo.color}` })
                          )}
                          {statusInfo.iconType === 'clock' && (
                            React.createElement(Clock, { className: `w-4 h-4 ${statusInfo.color}` })
                          )}
                          {statusInfo.iconType === 'x-circle' && (
                            React.createElement(XCircle, { className: `w-4 h-4 ${statusInfo.color}` })
                          )}
                          {statusInfo.iconType === 'alert-circle' && (
                            React.createElement(AlertCircle, { className: `w-4 h-4 ${statusInfo.color}` })
                          )}
                          <span className={`text-sm font-medium ${statusInfo.color}`}>{statusInfo.text}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 min-w-[170px]">
                        <div className="flex items-center">
                          {(interview.feedback?.overallScore && interview.feedback.overallScore > 0) && (
                            <div className="w-16 h-2 bg-background-light dark:bg-background-dark rounded-full overflow-hidden mr-2">
                              <div 
                                className={`h-full rounded-full ${
                                  interview.feedback.overallScore >= 80 ? 'bg-green-500' : 
                                interview.feedback.overallScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${interview.feedback.overallScore}%` }}
                            ></div>
                          </div>
                          )}
                          <span>{interview.feedback?.overallScore || 0}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        {interview.status === 'completed' ? (
                          <>
                          {interview.feedback?.overallScore && interview.feedback.overallScore > 0 ? (
                          <button 
                            className={`text-primary-light dark:text-primary-dark hover:underline text-sm font-medium`}
                            onClick={() => onViewFeedback(interview._id)}
                            disabled={interview.status !== 'completed'}
                          >
                            View Feedback
                          </button>
                        ) : (
                          <button 
                            className={`text-text-secondary-light dark:text-text-secondary-dark text-sm font-medium`}
                            onClick={() => onViewFeedback(interview._id)}
                            disabled={true}
                          >
                            No Feedback
                          </button>
                          )}
                          </>
                        ) : interview.status === 'not_started' ? (
                          <button 
                            className={`text-primary-light dark:text-primary-dark hover:underline text-sm font-medium`}
                            onClick={() => onRejoinInterview?.(interview._id)}
                          >
                            Start Interview
                          </button>
                        ) : interview.status === 'in_progress' && isInterviewJoinable(interview.scheduledAt || '') ? (
                          <button 
                            className={`text-primary-light dark:text-primary-dark hover:underline text-sm font-medium`}
                            onClick={() => onRejoinInterview?.(interview._id)}
                          >
                            Rejoin Interview
                          </button>
                        ) : (interview.status === 'cancelled' || interview.status === 'scheduled' || interview.status === 'expired' || (interview.status === 'in_progress' && !isInterviewJoinable(interview.scheduledAt || ''))) ? (
                          <button 
                            className={`text-primary-light dark:text-primary-dark hover:underline text-sm font-medium`}
                            onClick={() => onRescheduleInterview?.(interview._id)}
                          >
                            Reschedule Interview
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                } catch (error) {
                  console.error('InterviewHistory: Error rendering interview row:', error, interview);
                  return null;
                }
              })}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
            {/* Mobile Layout */}
            {isMobileView ? (
              <div className="space-y-3">
                {/* Page info - centered on mobile */}
                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                  Page {currentPage} of {totalPages}
                </div>
                
                {/* Page numbers row */}
                <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
                  {generatePageNumbers().map((page, index) => {
                    if (typeof page === 'string') {
                      // Handle ellipsis
                      return (
                        <span
                          key={page}
                          className="px-2 py-1 text-text-secondary-light dark:text-text-secondary-dark text-sm flex-shrink-0"
                        >
                          ...
                        </span>
                      );
                    }
                    
                    // Handle regular page numbers
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "primary" : "outline"}
                        size="sm"
                        className={`px-2 py-1 text-sm min-w-[32px] flex-shrink-0 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                
                {/* Navigation buttons row */}
                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<ChevronLeft size={14} />}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    className="flex-1"
                  >
                    Prev
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    rightIcon={<ChevronRight size={14} />}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : (
              /* Desktop Layout - Original row design */
              <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<ChevronLeft size={14} />}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  >
                    Previous
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="flex items-center gap-1">
                    {generatePageNumbers().map((page, index) => {
                      if (typeof page === 'string') {
                        // Handle ellipsis
                        return (
                          <span
                            key={page}
                            className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                          >
                            ...
                          </span>
                        );
                      }
                      
                      // Handle regular page numbers
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "primary" : "outline"}
                          size="sm"
                          className={`px-3 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    rightIcon={<ChevronRight size={14} />}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {filteredInterviews.length === 0 && !isLoading && (
          <div className="text-base text-center py-8 text-text-secondary-light dark:text-text-secondary-dark">
            No interviews conducted in this time period.
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          {!isMobileView && (
            <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
              {React.createElement(History, { className: "w-5 h-5 text-primary-light dark:text-primary-dark" })}
            </div>
          )}
          <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
            Interview History
          </h3>
        </div>
        
        <div className="relative">
          <select
            value={selectedDateFilter}
            onChange={(e) => onDateFilterChange(e.target.value)}
            className="appearance-none bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark"
            disabled={isLoading}
          >
            {dateFilters.map(filter => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
      </div>
      
      {renderContent()}
    </div>
  );
}; 